import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../../auth/authContext.tsx";
import { useGridPermissions } from "../../hooks/usePermissions.ts";
import {
  getKioskLocation,
  saveKioskLocation,
  getDVDMerchandisers,
  getLocationPricingRules,
  saveLocationPricingRules,
  getLocationKioskLanguage,
  saveLocationKioskLanguage,
  type KioskLocation,
  type DVDMerchandiser,
  type LocationKioskLanguage,
} from "../../api/location.ts";
import {
  getTicketTechs,
  getTechsForLocation,
  createTicketTech,
  saveTicketTech,
  saveTechniciansForLocation,
  type TicketTech,
} from "../../api/technician.ts";
import type { KioskCacheItem, LocationCacheItem } from "../../types/session.ts";
import { PagePopup } from "../../components/dataGrid/pagePopup.tsx";
import { GridEditModal } from "../../components/editSearch/gridEditModal.tsx";
import { ComboBoxInput } from "../../components/ui/comboBoxInput.tsx";
import { CommonValueSelect } from "../../components/ui/commonValueSelect.tsx";
import { SearchableSelect, type SearchableSelectOption } from "../../components/ui/searchableSelect.tsx";
import { getLanguageDisplayParts } from "../localization/languageCatalog.ts";
import { ensureCommonValue } from "../../api/common.ts";
import { useToast } from "../../hooks/useToast.ts";
import styles from "../../styles/formPage.module.scss";
import { LocationPricesTab } from "./locationPricesTab.tsx";
import { LocationPricingRulesTab } from "./locationPricingRulesTab.tsx";
import { validatePricingRules, dtoToRule, rulesToDtos, pricingRulesSignature } from "./pricingRulesLogic.ts";
import type { PricingRule } from "../../api/location.ts";
import { coordinateError, coordinatePairErrors, normalizeCoordinate } from "../../util/coordinate.ts";

/* ── helpers ──────────────────────────────────────────────── */

interface CommonCacheItem {
  TypeName?: string;
  Value?: string;
  Title?: string;
  sName?: string;
  sFilterValue?: string;
  sDisplayText?: string;
}

function ccName(c: CommonCacheItem) { return c.TypeName ?? c.sName ?? ""; }
function ccValue(c: CommonCacheItem) { return c.Value ?? c.sFilterValue ?? ""; }
function ccTitle(c: CommonCacheItem) { return c.Title ?? c.sDisplayText ?? ""; }

/** "ENG" → "English (ENG)"; a value the catalog does not know is shown as stored. */
function kioskLanguageLabel(code: string): string {
  const { name } = getLanguageDisplayParts(code);
  return name && name.toLowerCase() !== code.trim().toLowerCase() ? `${name} (${code})` : code;
}

/** "" (use the default) first, then the endpoint's languages. A current value
 *  that is no longer an active language stays selectable so opening the
 *  editor never silently changes it. */
function buildKioskLanguageOptions(info: LocationKioskLanguage | null, current: string): SearchableSelectOption[] {
  if (!info) return [];
  const def = (info.DefaultValue ?? "").trim();
  const options: SearchableSelectOption[] = [
    { value: "", label: def ? `Use default — ${kioskLanguageLabel(def)}` : "Use default" },
    ...info.Languages.map((l) => ({ value: l, label: kioskLanguageLabel(l) })),
  ];
  if (current && !info.Languages.some((l) => l.toLowerCase() === current.toLowerCase())) {
    options.push({ value: current, label: `${kioskLanguageLabel(current)} — not an active language` });
  }
  return options;
}

interface TimeZoneItem {
  CombinedID?: number;
  Name?: string;
  ID?: string;
  DisplayName?: string;
}

function emptyLocation(): KioskLocation {
  return {
    LocationID: -1,
    StoreName: "",
    StoreNumber: "",
    ShortLocationName: "",
    Address: "",
    AddressTwo: "",
    ZipCode: "",
    City: "",
    Suburb: "",
    County: "",
    Country: "",
    District: "",
    Chain: "",
    TimeZone: "",
    SegmentID: 0,
    AccessLevelID: 0,
    Merchandiser: 0,
    FootTraffic: "",
    GLN: "",
    GSOne: "",
    RegName: "",
    OrgNumber: "",
    StorePhoneNumber: "",
    OpeningHours: "",
    CustomerNumber: "",
    Longitude: "",
    Latitude: "",
    dLocationInstallDate: null,
    LocationRemovalDate: null,
    Note: "",
    ManagerOne: "",
    PhoneOne: "",
    EmailOne: "",
    ManagerTwo: "",
    PhoneTwo: "",
    EmailTwo: "",
    Commission: 0,
    AdminCost: 0,
    Percent: false,
    QuarterCommission: false,
    Account: "",
    OwnerOperatorID: 0,
    TS: 0,
  };
}


/* ── Page component ───────────────────────────────────────── */

/** Stable list for useGridPermissions (identity must not change per render). */
const LOC_PRICE_GRIDS = ["LOCATIONPRICEGRID"];

export function EditLocationPage({ popupEditId, onClose, onSaved }: { popupEditId?: string; onClose?: () => void; onSaved?: () => void } = {}) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { cache, appendCommonValue, upsertLocation, isSuperAdmin } = useAuth();
  const { toast } = useToast();

  const [loc, setLoc] = useState<KioskLocation>(emptyLocation);
  const [originalStoreName, setOriginalStoreName] = useState("");
  const [originalAddress, setOriginalAddress] = useState("");
  const [merchandisers, setMerchandisers] = useState<DVDMerchandiser[]>([]);
  const [status, setStatus] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [createdEditId, setCreatedEditId] = useState<string | null>(null);
  const [tab, setTab] = useState<"edit" | "additional" | "kiosks" | "prices" | "technicians" | "pricingRules">("edit");
  const [pricesVisited, setPricesVisited] = useState(false);
  const [timezone, setTimezone] = useState("");
  const [pricingRules, setPricingRules] = useState<PricingRule[]>([]);

  const baseEditId = popupEditId ?? searchParams.get("edit");
  const editId = createdEditId ?? baseEditId;
  const isCreate = !editId;

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);

  const lastLoadLocationKeyRef = useRef<string>("");
  // Signature of the pricing rules as last loaded/saved. The location save only
  // writes pricing rules when the current set differs from this — so a plain
  // create/edit never touches the optional, per-DB Kiosk_Time_Based_Pricing
  // table. Empty default ("[]") matches a brand-new location with no rules.
  const pricingRulesBaselineRef = useRef<string>(pricingRulesSignature([]));

  // Kiosk language: the location-level `Language` kiosk setting. "" = no
  // override (the location's kiosks use the default). Written after the
  // location save only when it differs from the baseline (as loaded/saved).
  const [kioskLanguage, setKioskLanguage] = useState("");
  const [kioskLanguageInfo, setKioskLanguageInfo] = useState<LocationKioskLanguage | null>(null);
  const [kioskLanguageError, setKioskLanguageError] = useState<string | null>(null);
  const kioskLanguageBaselineRef = useRef("");

  // Load location data + merchandisers
  useEffect(() => {
    const dedupeKey = `${editId ?? ""}|${isCreate}`;
    if (lastLoadLocationKeyRef.current === dedupeKey) return;
    lastLoadLocationKeyRef.current = dedupeKey;

    getDVDMerchandisers()
      .then((r) => {
        if (!r.bError && r.oData) setMerchandisers(r.oData);
      })
      .catch(() => { });

    // Secondary resource, loaded on its own: a failure only disables the
    // kiosk-language field, never the location itself. 0 = being created.
    getLocationKioskLanguage(isCreate ? 0 : Number(editId))
      .then((r) => {
        if (!r.bError && r.oData) {
          // Match the option's casing so the select shows the stored value.
          const raw = r.oData.Value ?? "";
          const value = r.oData.Languages.find((l) => l.toLowerCase() === raw.toLowerCase()) ?? raw;
          setKioskLanguageInfo(r.oData);
          setKioskLanguage(value);
          kioskLanguageBaselineRef.current = value;
          setKioskLanguageError(null);
        } else {
          setKioskLanguageError(r.sMessage || "Could not load the kiosk language.");
        }
      })
      .catch(() => setKioskLanguageError("Could not load the kiosk language."));

    if (!isCreate) {
      setLoading(true);
      const locId = Number(editId);
      Promise.all([
        getKioskLocation(locId),
        // Pricing rules are a secondary resource — a failure here must never
        // prevent the location from opening. Catch and return null so the
        // location load always succeeds.
        getLocationPricingRules(locId).catch(() => null),
      ])
        .then(([locR, rulesR]) => {
          if (!locR.bError && locR.oData) {
            setLoc(locR.oData);
            setOriginalStoreName(locR.oData.StoreName);
            setOriginalAddress(locR.oData.Address);
            setTimezone(locR.oData.TimeZone ?? "");
          } else {
            setStatus({ type: "error", message: locR.sMessage ?? "Failed to load location" });
          }
          if (rulesR && !rulesR.bError && rulesR.oData) {
            const loaded = rulesR.oData.map(dtoToRule);
            setPricingRules(loaded);
            pricingRulesBaselineRef.current = pricingRulesSignature(loaded);
          }
        })
        .catch((e: unknown) => setStatus({ type: "error", message: String(e) }))
        .finally(() => setLoading(false));
    }
  }, [editId, isCreate]);

  // Cache-derived dropdown options
  const commonLookup = useMemo(() => {
    const common = (cache?.CommonData?.Common ?? []) as CommonCacheItem[];
    const byName = (name: string) =>
      common
        .filter((c) => ccName(c).toLowerCase() === name.toLowerCase())
        .map((c) => ccTitle(c) || ccValue(c))
        .filter(Boolean)
        .sort();

    // Access levels: Title = display label, Value = numeric ID
    const accessLevels = common
      .filter((c) => ccName(c).toLowerCase() === "segment_accesslevel")
      .map((c) => ({ value: ccValue(c), label: ccTitle(c) }))
      .filter((a) => a.value && a.label)
      .sort((a, b) => a.label.localeCompare(b.label));

    // Segments: Title = numeric SegmentID, Value = display name (reversed!)
    const segments = common
      .filter((c) => ccName(c).toLowerCase() === "segment")
      .map((c) => ({ value: ccTitle(c), label: ccValue(c) }))
      .filter((s) => s.value && s.label)
      .sort((a, b) => a.label.localeCompare(b.label));

    // Mappings: Value = "SegmentID AccessLevelID" (space-delimited)
    const segmentMappings = common
      .filter((c) => ccName(c).toLowerCase() === "segment_mapping")
      .map((c) => {
        const parts = ccValue(c).split(" ");
        return { segmentId: parts[0], accessLevelId: parts[1] };
      })
      .filter((m) => m.segmentId && m.accessLevelId);

    /* Per-location free-text suggestions — values that already appear
     * elsewhere in the dataset, surfaced as autocomplete hints. The user
     * is free to type a new value; we don't push it into S_Admin_CB_Values
     * since these are per-location free-text fields, not enumerated
     * lookups. The new value lands in the dataset implicitly when the
     * location saves. FootTraffic / OpeningHours aren't on the lightweight
     * LocationCacheItem shape, so they stay as plain text inputs. */
    const distinctLocField = (
      key: "Suburb" | "County" | "City" | "Address" | "ZipCode",
    ): string[] => {
      const seen = new Set<string>();
      const out: string[] = [];
      for (const l of (cache?.CommonData?.Location ?? []) as LocationCacheItem[]) {
        const v = (l[key] ?? "").trim();
        if (!v || seen.has(v)) continue;
        seen.add(v);
        out.push(v);
      }
      return out.sort((a, b) => a.localeCompare(b));
    };

    return {
      chains: byName("sChain"),
      countries: byName("sCountry"),
      // Resolver mapping: the "District" field edits L.sCounty (sDistrict CB list);
      // the "State" field edits L.sDistrict (sState CB list).
      districts: byName("sDistrict"),
      states: byName("sState"),
      suburbs: distinctLocField("Suburb"),
      cities: distinctLocField("City"),
      addresses: distinctLocField("Address"),
      zipCodes: distinctLocField("ZipCode"),
      segments,
      accessLevels,
      segmentMappings,
    };
  }, [cache]);

  // Show the Prices tab only when the user has read access to the LOCATIONPRICEGRID page.
  const gridPerms = useGridPermissions(LOC_PRICE_GRIDS);
  const canSeePrices = gridPerms["locationpricegrid"];

  const timezoneOptions = useMemo(() => {
    const seen = new Set<string>();
    const options: { id: string; label: string }[] = [];
    for (const tz of (cache?.TimeZones ?? []) as TimeZoneItem[]) {
      const name = tz.Name ?? tz.DisplayName ?? "";
      if (name && !seen.has(name)) {
        seen.add(name);
        options.push({ id: name, label: name });
      }
    }
    return options.sort((a, b) => a.label.localeCompare(b.label));
  }, [cache]);

  // Filter segments by selected access level
  const filteredSegments = useMemo(() => {
    if (!loc.AccessLevelID) return commonLookup.segments;
    const mapped = commonLookup.segmentMappings
      .filter((m) => m.accessLevelId === String(loc.AccessLevelID))
      .map((m) => m.segmentId);
    if (mapped.length === 0) return commonLookup.segments;
    return commonLookup.segments.filter((s) => mapped.includes(s.value));
  }, [loc.AccessLevelID, commonLookup]);

  const set = <K extends keyof KioskLocation>(key: K, val: KioskLocation[K]) =>
    setLoc((prev) => ({ ...prev, [key]: val }));

  const handleSave = async () => {
    if (!loc.StoreName.trim()) {
      setStatus({ type: "error", message: "Store Name is required." });
      return;
    }

    if (!isSuperAdmin) {
      if (!loc.AccessLevelID) {
        setStatus({ type: "error", message: "Access Level is required." });
        return;
      }
      if (!loc.SegmentID) {
        setStatus({ type: "error", message: "Segment is required." });
        return;
      }
    }

    const { hasErrors: hasPricingErrors } = validatePricingRules(pricingRules);
    if (hasPricingErrors) {
      setTab("pricingRules");
      setStatus({ type: "error", message: "Fix the pricing rule errors before saving." });
      return;
    }

    // Coordinate clearing: if store name or address changed and had valid coords
    if (!isCreate) {
      const nameChanged = loc.StoreName !== originalStoreName;
      const addrChanged = loc.Address !== originalAddress;
      if ((nameChanged || addrChanged) && (loc.Longitude || loc.Latitude)) {
        loc.Longitude = "";
        loc.Latitude = "";
        setLoc({ ...loc });
      }
    }

    // Belt and braces for the onBlur normalisation: the offload map can't parse
    // a coordinate saved with stray whitespace.
    const longitude = normalizeCoordinate(loc.Longitude);
    const latitude = normalizeCoordinate(loc.Latitude);
    if (longitude !== (loc.Longitude ?? "") || latitude !== (loc.Latitude ?? "")) {
      loc.Longitude = longitude;
      loc.Latitude = latitude;
      setLoc({ ...loc });
    }
    const coordErrors = coordinatePairErrors(latitude, longitude);
    if (coordErrors.latitude || coordErrors.longitude) {
      setTab("edit");
      setStatus({ type: "error", message: coordErrors.latitude ?? coordErrors.longitude! });
      return;
    }

    setSaving(true);
    setStatus(null);
    try {
      const result = await saveKioskLocation(loc, timezone);
      if (!result.bError && result.oData && result.oData > 0) {
        const newId = result.oData;
        upsertLocation({
          LocationID: newId,
          StoreName: loc.StoreName,
          OwnerOperatorID: loc.OwnerOperatorID,
          Address: loc.Address,
          City: loc.City,
          District: loc.District,
          ZipCode: loc.ZipCode || undefined,
          County: loc.County || undefined,
          Suburb: loc.Suburb || undefined,
          Chain: loc.Chain || undefined,
          ShortName: loc.ShortLocationName || undefined,
          Country: loc.Country || undefined,
          Latitude: loc.Latitude || undefined,
          Longitude: loc.Longitude || undefined,
          AccessLevelID: loc.AccessLevelID,
          SegmentID: loc.SegmentID,
        });

        // Save pricing rules to their own table (soft-delete + re-insert) ONLY
        // when they actually changed. A plain location create/edit must not touch
        // the optional, per-DB Kiosk_Time_Based_Pricing table (absent on some DBs
        // → "Invalid object name"). Also check oData === false: a Boolean-returning
        // method that returns False sets bError: false with oData: false — both fail.
        if (pricingRulesSignature(pricingRules) !== pricingRulesBaselineRef.current) {
          const rulesResult = await saveLocationPricingRules(newId, rulesToDtos(newId, pricingRules));
          if (!rulesResult || rulesResult.bError || rulesResult.oData === false) {
            setStatus({ type: "error", message: rulesResult?.sMessage ?? "Location saved but pricing rules could not be saved." });
            return;
          }
          pricingRulesBaselineRef.current = pricingRulesSignature(pricingRules);
        }

        // Kiosk language — needs the LocationID, so it is written after the
        // location exists, and only when changed. A failure here does not undo
        // the location (nor stop Create from turning into Edit below); it is
        // reported in place of the success banner.
        let followUpError: string | null = null;
        if (kioskLanguageInfo?.Level === "LOCATION" && kioskLanguage !== kioskLanguageBaselineRef.current) {
          try {
            const langResult = await saveLocationKioskLanguage(newId, kioskLanguage);
            if (!langResult.bError && langResult.oData) {
              kioskLanguageBaselineRef.current = kioskLanguage;
            } else {
              followUpError = `Location #${newId} saved, but the kiosk language was not: ${langResult.sMessage || "unknown error"}`;
            }
          } catch (e: unknown) {
            followUpError = `Location #${newId} saved, but the kiosk language was not: ${String(e)}`;
          }
        }
        const savedStatus = followUpError
          ? { type: "error" as const, message: followUpError }
          : { type: "success" as const, message: `Saved — Location #${newId}` };

        // Register any new District/State values into the local CB cache (per the
        // resolver mapping: District→sDistrict from County, State→sState from
        // District). Splices the server row directly — no full refresh. Chain and
        // Country are not auto-registered: a new value there only enters the list
        // through CommonValueSelect's confirmed "+ Other…".
        void Promise.allSettled([
          ensureCommonValue(cache, appendCommonValue, "sDistrict", loc.County),
          ensureCommonValue(cache, appendCommonValue, "sState", loc.District),
        ]);

        onSaved?.();
        toast(isCreate ? "Location created" : "Location saved", "success");

        if (isCreate) {
          setLoc((prev) => ({ ...prev, LocationID: newId }));
          // Pre-prime the load-effect dedupe key so the upcoming
          // editId/isCreate flip doesn't re-fire it (would trigger
          // setLoading(true) and flash the body to "Loading...").
          lastLoadLocationKeyRef.current = `${newId}|false`;
          setCreatedEditId(String(newId));
          setOriginalStoreName(loc.StoreName);
          setOriginalAddress(loc.Address);
          setStatus(savedStatus);
          if (!onClose && typeof window !== "undefined") {
            window.history.replaceState({}, "", `/page/editlocation?edit=${newId}`);
          }
          getKioskLocation(newId)
            .then((r) => {
              if (!r.bError && r.oData) {
                setLoc(r.oData);
                setOriginalStoreName(r.oData.StoreName);
                setOriginalAddress(r.oData.Address);
                setTimezone(r.oData.TimeZone ?? "");
              }
            })
            .catch(() => { });
        } else {
          setStatus(savedStatus);
        }
      } else {
        setStatus({ type: "error", message: result.sMessage ?? "Could not save location." });
      }
    } catch (e: unknown) {
      setStatus({ type: "error", message: String(e) });
    } finally {
      setSaving(false);
    }
  };

  const viewMap = () => {
    if (loc.Latitude && loc.Longitude) {
      window.open(
        `https://www.google.com/maps?q=${loc.Latitude},${loc.Longitude}`,
        "_blank",
        "noopener"
      );
    } else if (loc.Address && loc.City) {
      window.open(
        `https://www.google.com/maps?q=${encodeURIComponent(loc.Address + " " + loc.City)}`,
        "_blank",
        "noopener"
      );
    }
  };

  if (loading) return <div className={styles.formContainer}>Loading...</div>;

  const tabs = isCreate
    ? [
      { key: "edit" as const, label: "Edit" },
      { key: "additional" as const, label: "Additional" },
      { key: "pricingRules" as const, label: "Pricing Rules" },
    ]
    : [
      { key: "edit" as const, label: "Edit" },
      { key: "additional" as const, label: "Additional" },
      { key: "kiosks" as const, label: "Kiosks" },
      ...(canSeePrices ? [{ key: "prices" as const, label: "Prices" }] : []),
      { key: "technicians" as const, label: "Technicians" },
      { key: "pricingRules" as const, label: "Pricing Rules" },
    ];

  /* ── Unified Modal (route + popup entry) ───────────────
     Route entry (/page/editlocation?edit=X) and PagePopup entry both render
     the same Modal. Save/Cancel + View Map auxiliary live in `ModalFooter`
     per AGENTS.md § Create/Edit Modal Guidelines. Closing from route mode
     falls back to `navigate(-1)`. */
  const closeModal = onClose ?? (() => navigate(-1));
  const title = isCreate ? "Create new location" : `Edit Location — ID ${loc.LocationID}`;

  return (
    <GridEditModal
      title={title}
      wide
      maxWidth="1400px"
      saving={saving}
      saveLabel={isCreate ? "Create" : "Save Location"}
      savingLabel={isCreate ? "Creating..." : "Saving..."}
      statusMessage={status}
      minBodyHeight="520px"
      footerLeft={!isCreate ? (
        <button className={styles.secondaryBtn} onClick={viewMap}>🗺️ View Map</button>
      ) : undefined}
      onClose={closeModal}
      onSave={handleSave}
    >
      <>
        {/* Tabs */}
        <div style={{ display: "flex", gap: "var(--space-1)", marginBottom: "var(--space-4)", borderBottom: "1px solid var(--color-border-default)" }}>
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => { setTab(t.key); if (t.key === "prices") setPricesVisited(true); }}
              style={{
                padding: "var(--space-2) var(--space-4)",
                border: "none",
                borderBottom: tab === t.key ? "2px solid var(--color-primary)" : "2px solid transparent",
                background: "none",
                cursor: "pointer",
                fontWeight: tab === t.key ? "var(--font-semibold)" : "var(--font-normal)",
                color: tab === t.key ? "var(--color-primary)" : "var(--color-text-secondary)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div style={{ display: tab === "edit" ? undefined : "none" }}>
          <LocationEditTab
            loc={loc}
            set={set}
            commonLookup={commonLookup}
            filteredSegments={filteredSegments}
            timezoneOptions={timezoneOptions}
            timezone={timezone}
            setTimezone={setTimezone}
            kioskLanguage={kioskLanguage}
            setKioskLanguage={setKioskLanguage}
            kioskLanguageInfo={kioskLanguageInfo}
            kioskLanguageError={kioskLanguageError}
          />
        </div>
        <div style={{ display: tab === "additional" ? undefined : "none" }}>
          <LocationAdditionalTab loc={loc} set={set} merchandisers={merchandisers} />
        </div>
        {!isCreate && (
          <>
            <div style={{ display: tab === "kiosks" ? undefined : "none" }}>
              <LocationKiosksTab locationId={loc.LocationID} />
            </div>
            {canSeePrices && (
              <div style={{ display: tab === "prices" ? undefined : "none" }}>
                {pricesVisited && <LocationPricesTab locationId={loc.LocationID} />}
              </div>
            )}
            <div style={{ display: tab === "technicians" ? undefined : "none" }}>
              <LocationTechniciansTab locationId={loc.LocationID} />
            </div>
          </>
        )}
        <div style={{ display: tab === "pricingRules" ? undefined : "none" }}>
          <LocationPricingRulesTab
            rules={pricingRules}
            onChange={setPricingRules}
          />
        </div>

      </>
    </GridEditModal>
  );
}

/* ── Edit Tab ─────────────────────────────────────────────── */

function LocationEditTab({
  loc,
  set,
  commonLookup,
  filteredSegments,
  timezoneOptions,
  timezone,
  setTimezone,
  kioskLanguage,
  setKioskLanguage,
  kioskLanguageInfo,
  kioskLanguageError,
}: {
  loc: KioskLocation;
  set: <K extends keyof KioskLocation>(key: K, val: KioskLocation[K]) => void;
  commonLookup: {
    chains: string[];
    countries: string[];
    districts: string[];
    states: string[];
    suburbs: string[];
    cities: string[];
    addresses: string[];
    zipCodes: string[];
    accessLevels: { value: string; label: string }[];
  };
  filteredSegments: { value: string; label: string }[];
  timezoneOptions: { id: string; label: string }[];
  timezone: string;
  setTimezone: (v: string) => void;
  kioskLanguage: string;
  setKioskLanguage: (v: string) => void;
  kioskLanguageInfo: LocationKioskLanguage | null;
  kioskLanguageError: string | null;
}) {
  const { toast } = useToast();
  const { isSuperAdmin } = useAuth();
  const kioskLanguageOptions = useMemo(
    () => buildKioskLanguageOptions(kioskLanguageInfo, kioskLanguage),
    [kioskLanguageInfo, kioskLanguage],
  );
  const kioskLanguageEditable = kioskLanguageInfo?.Level === "LOCATION";
  const [geocoding, setGeocoding] = useState(false);
  // An error stays hidden while its own field is being typed in, and the
  // "other half required" error stays hidden while the other field is.
  const [focusedCoord, setFocusedCoord] = useState<"Latitude" | "Longitude" | null>(null);
  const normLat = normalizeCoordinate(loc.Latitude);
  const normLng = normalizeCoordinate(loc.Longitude);
  const pairErrors = coordinatePairErrors(normLat, normLng);
  const latError = focusedCoord === "Latitude" ? null : focusedCoord === "Longitude" ? coordinateError(normLat, "latitude") : pairErrors.latitude;
  const lngError = focusedCoord === "Longitude" ? null : focusedCoord === "Latitude" ? coordinateError(normLng, "longitude") : pairErrors.longitude;

  async function handleGeocode() {
    const c = {
      street: [loc.Address, loc.AddressTwo].map((s) => (s ?? "").trim()).filter(Boolean).join(" "),
      city: (loc.City ?? "").trim(),
      suburb: (loc.Suburb ?? "").trim(),
      county: (loc.County ?? "").trim(),
      state: (loc.District ?? "").trim(),
      postalcode: (loc.ZipCode ?? "").trim(),
      country: (loc.Country ?? "").trim(),
    };
    if (!Object.values(c).some(Boolean)) { toast("Fill in at least one address field first.", "error"); return; }
    setGeocoding(true);
    try {
      // Structured lookup + countrycodes lands the right spot far more often than one free-text
      // string; free text stays only as a fallback when the structured query is too strict to hit.
      const iso2 = /^[a-z]{2}$/i.test(c.country) ? c.country.toLowerCase() : "";
      const search = async (params: Record<string, string>) => {
        const url = new URL("https://nominatim.openstreetmap.org/search");
        url.searchParams.set("format", "jsonv2");
        url.searchParams.set("addressdetails", "1");
        url.searchParams.set("limit", "1");
        if (iso2) url.searchParams.set("countrycodes", iso2);
        for (const [k, v] of Object.entries(params)) if (v) url.searchParams.set(k, v);
        const resp = await fetch(url, { headers: { Accept: "application/json" } });
        if (!resp.ok) throw new Error(`Address lookup failed (${resp.status}).`);
        return (await resp.json()) as Array<{ lat: string; lon: string; display_name: string; addresstype?: string }>;
      };

      let hits = await search({ street: c.street, city: c.city, county: c.county, state: c.state, postalcode: c.postalcode, country: c.country });
      if (hits.length === 0) {
        hits = await search({ q: [c.street, c.suburb, c.postalcode, c.city, c.state, c.country].filter(Boolean).join(", ") });
      }
      if (hits.length === 0) { toast("No results found for the given address.", "info"); return; }

      const best = hits[0]!;
      set("Latitude", best.lat);
      set("Longitude", best.lon);
      // Street given but only an area resolved -> flag it so a coarse hit is not saved blindly.
      const coarseMatch = c.street !== "" && ["country", "state", "county", "city", "suburb", "postcode"].includes(best.addresstype ?? "");
      toast(`${coarseMatch ? "Approximate — check the map. " : ""}Matched: ${best.display_name}`, coarseMatch ? "warning" : "success");
    } catch (err: unknown) {
      toast(err instanceof Error && err.message ? err.message : "Failed to look up the address. Please try again.", "error");
    } finally {
      setGeocoding(false);
    }
  }

  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>Location Info</h3>

      <div className={styles.fieldRow}>
        <label>Store Name *</label>
        <input
          className={styles.fieldInput}
          value={loc.StoreName}
          onChange={(e) => set("StoreName", e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Store Number</label>
        <input
          className={styles.fieldInput}
          value={loc.StoreNumber ?? ""}
          onChange={(e) => set("StoreNumber", e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Short Name</label>
        <input
          className={styles.fieldInput}
          value={loc.ShortLocationName ?? ""}
          onChange={(e) => set("ShortLocationName", e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Chain</label>
        <CommonValueSelect
          className={styles.fieldCombo}
          column="sChain"
          listLabel="Chain"
          value={loc.Chain ?? ""}
          options={commonLookup.chains}
          onChange={(v) => set("Chain", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Country</label>
        <CommonValueSelect
          className={styles.fieldCombo}
          column="sCountry"
          listLabel="Country"
          value={loc.Country ?? ""}
          options={commonLookup.countries}
          onChange={(v) => set("Country", v)}
        />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Classification</h3>

      <div className={styles.fieldRow}>
        <label>Timezone</label>
        <select className={styles.fieldSelect} value={timezone} onChange={(e) => setTimezone(e.target.value)}>
          <option value="">— select —</option>
          {timezoneOptions.map((tz) => (
            <option key={tz.id} value={tz.id}>{tz.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.fieldRow}>
        <label>Kiosk language</label>
        {kioskLanguageEditable ? (
          <SearchableSelect
            className={styles.fieldCombo}
            ariaLabel="Kiosk language"
            options={kioskLanguageOptions}
            value={kioskLanguage}
            onChange={setKioskLanguage}
          />
        ) : (
          <span className={styles.hint}>
            {kioskLanguageError
              ?? (kioskLanguageInfo === null
                ? "Loading…"
                : kioskLanguageInfo.Level === "KIOSK"
                  ? "On this database the kiosk language is set per kiosk (Settings → Kiosk / Location)."
                  : "This database has no Language kiosk setting.")}
          </span>
        )}
      </div>
      {kioskLanguageEditable && (
        <div className={styles.fieldRow}>
          <label />
          <span className={styles.hint}>
            The standard language for every kiosk at this location. Leave it on the default to follow the general setting.
          </span>
        </div>
      )}

      <div className={styles.fieldRow}>
        {/* Not marked required for a SuperAdmin — the save guard lets them through. */}
        <label>Access Level {!isSuperAdmin && <span style={{ color: "var(--color-error)" }}>*</span>}</label>
        <select
          className={styles.fieldSelect}
          value={loc.AccessLevelID}
          onChange={(e) => set("AccessLevelID", Number(e.target.value))}
        >
          <option value={0}>— select —</option>
          {commonLookup.accessLevels.map((al) => (
            <option key={al.value} value={al.value}>{al.label}</option>
          ))}
        </select>
      </div>

      <div className={styles.fieldRow}>
        <label>Segment {!isSuperAdmin && <span style={{ color: "var(--color-error)" }}>*</span>}</label>
        <select
          className={styles.fieldSelect}
          value={loc.SegmentID}
          onChange={(e) => set("SegmentID", Number(e.target.value))}
        >
          <option value={0}>— select —</option>
          {filteredSegments.map((s) => (
            <option key={s.value} value={s.value}>{s.label}</option>
          ))}
        </select>
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Address</h3>

      <div className={styles.fieldRow}>
        <label>Address</label>
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.Address ?? ""}
          options={commonLookup.addresses}
          onChange={(v) => set("Address", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Address 2</label>
        <input className={styles.fieldInput} value={loc.AddressTwo ?? ""} onChange={(e) => set("AddressTwo", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Zip Code</label>
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.ZipCode ?? ""}
          options={commonLookup.zipCodes}
          onChange={(v) => set("ZipCode", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>City</label>
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.City ?? ""}
          options={commonLookup.cities}
          onChange={(v) => set("City", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Suburb</label>
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.Suburb ?? ""}
          options={commonLookup.suburbs}
          onChange={(v) => set("Suburb", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>District</label>
        {/* Edits L.sCounty — the resolver's District dimension (sDistrict CB list).
            Labels match the price grid (Chain/District/State). CB lists are populated
            server-side on save (WebSaveKioskLocation → EnsureLocationCBValues), not
            per field-edit, so transient typos never pollute the lists. */}
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.County ?? ""}
          options={commonLookup.districts}
          onChange={(v) => set("County", v)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>State</label>
        {/* Edits L.sDistrict — the resolver's State dimension (sState CB list). */}
        <ComboBoxInput
          className={styles.fieldInput}
          value={loc.District ?? ""}
          options={commonLookup.states}
          onChange={(v) => set("District", v)}
        />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Coordinates</h3>

      <div className={styles.fieldRow}>
        <label>Longitude</label>
        <input
          className={styles.fieldInput}
          inputMode="decimal"
          placeholder="e.g. 18.0686"
          aria-invalid={lngError ? true : undefined}
          aria-describedby={lngError ? "location-longitude-error" : undefined}
          value={loc.Longitude ?? ""}
          onChange={(e) => set("Longitude", e.target.value)}
          onFocus={() => setFocusedCoord("Longitude")}
          onBlur={(e) => { set("Longitude", normalizeCoordinate(e.target.value)); setFocusedCoord(null); }}
        />
      </div>
      {lngError && <div id="location-longitude-error" className={styles.fieldErrorText} role="alert">{lngError}</div>}

      <div className={styles.fieldRow}>
        <label>Latitude</label>
        <input
          className={styles.fieldInput}
          inputMode="decimal"
          placeholder="e.g. 59.3293"
          aria-invalid={latError ? true : undefined}
          aria-describedby={latError ? "location-latitude-error" : undefined}
          value={loc.Latitude ?? ""}
          onChange={(e) => set("Latitude", e.target.value)}
          onFocus={() => setFocusedCoord("Latitude")}
          onBlur={(e) => { set("Latitude", normalizeCoordinate(e.target.value)); setFocusedCoord(null); }}
        />
      </div>
      {latError && <div id="location-latitude-error" className={styles.fieldErrorText} role="alert">{latError}</div>}

      <div className={styles.fieldRow}>
        <label />
        <button
          type="button"
          className={styles.secondaryBtn}
          disabled={geocoding}
          onClick={handleGeocode}
        >
          {geocoding ? "Looking up…" : "Fetch from Address"}
        </button>
      </div>
    </div>
  );
}

/* ── Additional Tab ───────────────────────────────────────── */

function LocationAdditionalTab({
  loc,
  set,
  merchandisers,
}: {
  loc: KioskLocation;
  set: <K extends keyof KioskLocation>(key: K, val: KioskLocation[K]) => void;
  merchandisers: DVDMerchandiser[];
}) {
  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>Business</h3>

      <div className={styles.fieldRow}>
        <label>Merchandiser</label>
        <select
          className={styles.fieldSelect}
          value={loc.Merchandiser ?? 0}
          onChange={(e) => set("Merchandiser", Number(e.target.value))}
        >
          <option value={0}>— none —</option>
          {merchandisers.map((m) => (
            <option key={m.MerchandiserID} value={m.MerchandiserID}>{m.Name}</option>
          ))}
        </select>
      </div>

      <div className={styles.fieldRow}>
        <label>Foot Traffic</label>
        <input
          className={styles.fieldInput}
          value={loc.FootTraffic ?? ""}
          onChange={(e) => set("FootTraffic", e.target.value)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>GLN</label>
        <input className={styles.fieldInput} value={loc.GLN ?? ""} onChange={(e) => set("GLN", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>GS1</label>
        <input className={styles.fieldInput} value={loc.GSOne ?? ""} onChange={(e) => set("GSOne", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Reg Name</label>
        <input className={styles.fieldInput} value={loc.RegName ?? ""} onChange={(e) => set("RegName", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Org Number</label>
        <input className={styles.fieldInput} value={loc.OrgNumber ?? ""} onChange={(e) => set("OrgNumber", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Store Phone</label>
        <input className={styles.fieldInput} value={loc.StorePhoneNumber ?? ""} onChange={(e) => set("StorePhoneNumber", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Opening Hours</label>
        <input className={styles.fieldInput} value={loc.OpeningHours ?? ""} onChange={(e) => set("OpeningHours", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Customer No.</label>
        <input className={styles.fieldInput} value={loc.CustomerNumber ?? ""} onChange={(e) => set("CustomerNumber", e.target.value)} />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Dates</h3>

      <div className={styles.fieldRow}>
        <label>Install Date</label>
        <input
          type="date"
          className={styles.fieldInput}
          value={loc.dLocationInstallDate?.substring(0, 10) ?? ""}
          onChange={(e) => set("dLocationInstallDate", e.target.value || null)}
        />
      </div>

      <div className={styles.fieldRow}>
        <label>Removal Date</label>
        <input
          type="date"
          className={styles.fieldInput}
          value={loc.LocationRemovalDate?.substring(0, 10) ?? ""}
          onChange={(e) => set("LocationRemovalDate", e.target.value || null)}
        />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Note</h3>

      <div className={styles.fieldRowStacked}>
        <label>Note</label>
        <textarea
          className={styles.fieldTextarea}
          value={loc.Note ?? ""}
          onChange={(e) => set("Note", e.target.value)}
          rows={3}
        />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Manager 1</h3>

      <div className={styles.fieldRow}>
        <label>Name</label>
        <input className={styles.fieldInput} value={loc.ManagerOne ?? ""} onChange={(e) => set("ManagerOne", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Phone</label>
        <input className={styles.fieldInput} value={loc.PhoneOne ?? ""} onChange={(e) => set("PhoneOne", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Email</label>
        <input className={styles.fieldInput} value={loc.EmailOne ?? ""} onChange={(e) => set("EmailOne", e.target.value)} />
      </div>

      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Manager 2</h3>

      <div className={styles.fieldRow}>
        <label>Name</label>
        <input className={styles.fieldInput} value={loc.ManagerTwo ?? ""} onChange={(e) => set("ManagerTwo", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Phone</label>
        <input className={styles.fieldInput} value={loc.PhoneTwo ?? ""} onChange={(e) => set("PhoneTwo", e.target.value)} />
      </div>

      <div className={styles.fieldRow}>
        <label>Email</label>
        <input className={styles.fieldInput} value={loc.EmailTwo ?? ""} onChange={(e) => set("EmailTwo", e.target.value)} />
      </div>
    </div>
  );
}

/* ── Kiosks Tab (edit only — lists kiosks at location) ──── */

function formatActivity(val?: string): string {
  if (!val) return "No activity";
  const d = new Date(val);
  if (isNaN(d.getTime())) return "No activity";
  const now = Date.now();
  const diffMs = now - d.getTime();
  if (diffMs < 5 * 60_000) return "Online";
  const days = Math.floor(diffMs / 86_400_000);
  if (days < 1) {
    const hrs = Math.floor(diffMs / 3_600_000);
    const mins = Math.floor((diffMs % 3_600_000) / 60_000);
    return hrs > 0 ? `${hrs}h ${mins}m ago` : `${mins}m ago`;
  }
  return `${days}d ago`;
}

function LocationKiosksTab({ locationId }: { locationId: number }) {
  const { cache } = useAuth();
  const [kioskPopupPath, setKioskPopupPath] = useState<string | null>(null);

  const kiosksAtLocation = useMemo(() => {
    if (locationId < 1) return [];
    const allKiosks = (cache?.CommonData?.Kiosk ?? []) as KioskCacheItem[];
    return allKiosks.filter((k) => k.LocationID === locationId);
  }, [locationId, cache]);

  // Location fields (StoreName, City, Address) aren't in the kiosk cache — look them up from the location cache
  const loc = useMemo(() => {
    if (locationId < 1) return null;
    const allLocations = (cache?.CommonData?.Location ?? []) as LocationCacheItem[];
    return allLocations.find((l) => l.LocationID === locationId) ?? null;
  }, [locationId, cache]);

  if (locationId < 1) {
    return <div className={styles.formContainerWide}><p>Save the location first to see kiosks.</p></div>;
  }

  return (
    <div className={styles.formContainerWide}>
      <h3 className={styles.sectionTitle}>Kiosks at this Location ({kiosksAtLocation.length})</h3>
      {kiosksAtLocation.length === 0 ? (
        <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" }}>No kiosks assigned to this location.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Kiosk ID</th>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>City</th>
              <th className={styles.th}>Address</th>
              <th className={styles.th}>Last Activity</th>
              <th className={styles.th}>Serial</th>
              <th className={styles.th}></th>
            </tr>
          </thead>
          <tbody>
            {kiosksAtLocation.map((k) => (
              <tr key={k.KioskID}>
                <td className={styles.td}>{k.KioskID}</td>
                <td className={styles.td}>{loc?.StoreName ?? "—"}</td>
                <td className={styles.td}>{loc?.City ?? "—"}</td>
                <td className={styles.td}>{loc?.Address ?? "—"}</td>
                <td className={styles.td}>{formatActivity(k.LastActivity)}</td>
                <td className={styles.td}>{k.SerialNumber ?? "—"}</td>
                <td className={styles.td}>
                  <button
                    className={styles.secondaryBtn}
                    style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }}
                    onClick={() => setKioskPopupPath(`/page/editkiosk?edit=${k.KioskID}`)}
                  >
                    Go to Kiosk
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {kioskPopupPath && (
        <PagePopup path={kioskPopupPath} onClose={() => setKioskPopupPath(null)} />
      )}
    </div>
  );
}

/* ── Technicians Tab ──────────────────────────────────────── */

function emptyTech(): TicketTech {
  return { ID: -1, Firstname: "", Lastname: "", Email: "", Phone: "", Phone2: "", ExtraInfo: "", Prio: 0, IsTechnician: true };
}

function LocationTechniciansTab({ locationId }: { locationId: number }) {
  const [allTechs, setAllTechs] = useState<TicketTech[]>([]);
  const [assignedTechs, setAssignedTechs] = useState<TicketTech[]>([]);
  const [filter, setFilter] = useState("");
  const [newTech, setNewTech] = useState<TicketTech>(emptyTech);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editTech, setEditTech] = useState<TicketTech | null>(null);
  const [msg, setMsg] = useState<{ text: string; error?: boolean } | null>(null);

  function loadData() {
    if (locationId < 1) return;
    setLoading(true);
    Promise.all([getTicketTechs(locationId), getTechsForLocation(locationId)])
      .then(([allR, locR]) => {
        if (!allR.bError && allR.oData) setAllTechs(allR.oData);
        if (!locR.bError && locR.oData) setAssignedTechs(locR.oData);
      })
      .catch(() => setMsg({ text: "Failed to load technicians", error: true }))
      .finally(() => setLoading(false));
  }

  useEffect(() => { loadData(); }, [locationId]);

  // Filter available techs: exclude already assigned, then text filter
  const assignedIds = useMemo(() => new Set(assignedTechs.map((t) => t.ID)), [assignedTechs]);
  const filteredTechs = useMemo(() => {
    const lf = filter.toLowerCase();
    return allTechs
      .filter((t) => !assignedIds.has(t.ID))
      .filter((t) =>
        !lf ||
        t.Firstname.toLowerCase().includes(lf) ||
        t.Lastname.toLowerCase().includes(lf) ||
        t.Email.toLowerCase().includes(lf) ||
        t.Phone.toLowerCase().includes(lf) ||
        t.Phone2.toLowerCase().includes(lf)
      );
  }, [allTechs, assignedIds, filter]);

  async function handleAssign(tech: TicketTech) {
    const updated = [...assignedTechs, { ...tech, Prio: assignedTechs.length }];
    setAssignedTechs(updated);
    setSaving(true);
    const r = await saveTechniciansForLocation(updated, locationId);
    if (r.bError) setMsg({ text: r.sMessage ?? "Failed to assign", error: true });
    setSaving(false);
  }

  async function handleUnassign(tech: TicketTech) {
    const updated = assignedTechs.filter((t) => t.ID !== tech.ID).map((t, i) => ({ ...t, Prio: i }));
    setAssignedTechs(updated);
    setSaving(true);
    const r = await saveTechniciansForLocation(updated, locationId);
    if (r.bError) setMsg({ text: r.sMessage ?? "Failed to remove", error: true });
    setSaving(false);
  }

  async function handleMovePrio(index: number, dir: -1 | 1) {
    const newIdx = index + dir;
    if (newIdx < 0 || newIdx >= assignedTechs.length) return;
    const arr = [...assignedTechs];
    const tmp = arr[index]!;
    arr[index] = arr[newIdx]!;
    arr[newIdx] = tmp;
    const updated = arr.map((t, i) => ({ ...t, Prio: i }));
    setAssignedTechs(updated);
    setSaving(true);
    const r = await saveTechniciansForLocation(updated, locationId);
    if (r.bError) setMsg({ text: r.sMessage ?? "Failed to reorder", error: true });
    setSaving(false);
  }

  async function handleCreateTech() {
    if (!newTech.Firstname.trim() || !newTech.Lastname.trim()) {
      setMsg({ text: "Firstname and Lastname are required.", error: true });
      return;
    }
    setSaving(true);
    setMsg(null);
    const r = await createTicketTech(newTech);
    if (!r.bError && r.oData) {
      setMsg({ text: "Technician created successfully" });
      setNewTech(emptyTech());
      loadData();
    } else {
      setMsg({ text: r.sMessage ?? "Could not create technician", error: true });
    }
    setSaving(false);
  }

  async function handleSaveEdit() {
    if (!editTech) return;
    setSaving(true);
    const r = await saveTicketTech(editTech);
    if (!r.bError && r.oData) {
      setMsg({ text: "Technician saved" });
      setEditTech(null);
      loadData();
    } else {
      setMsg({ text: r.sMessage ?? "Could not save technician", error: true });
    }
    setSaving(false);
  }

  if (locationId < 1) {
    return <div className={styles.formContainerWide}><p>Save the location first to manage technicians.</p></div>;
  }
  if (loading) return <div className={styles.formContainerWide}>Loading technicians...</div>;

  const techColumns: { key: keyof TicketTech; label: string }[] = [
    { key: "ID", label: "ID" },
    { key: "Firstname", label: "Firstname" },
    { key: "Lastname", label: "Lastname" },
    { key: "Email", label: "Email" },
    { key: "Phone", label: "Phone" },
    { key: "Phone2", label: "Phone #2" },
    { key: "ExtraInfo", label: "Extra Info" },
  ];

  return (
    <div className={styles.formContainerWide}>
      {msg && (
        <div className={`${styles.statusMsg} ${msg.error ? styles.statusError : styles.statusSuccess}`} style={{ marginBottom: "var(--space-3)" }}>
          {msg.text}
        </div>
      )}

      {/* ── Available Technicians ─────────────────────── */}
      <h3 className={styles.sectionTitle}>Technicians available</h3>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: "var(--space-2)" }}>
        Click the assign button to add a technician to this location. Click the ID to edit.
      </p>

      <div className={styles.fieldRow} style={{ marginBottom: "var(--space-3)" }}>
        <label>Filter</label>
        <input className={styles.fieldInput} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by name, email, phone…" />
      </div>

      <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {techColumns.map((c) => <th key={c.key} className={styles.th}>{c.label}</th>)}
            <th className={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {filteredTechs.length === 0 ? (
            <tr><td className={styles.td} colSpan={techColumns.length + 1} style={{ textAlign: "center" }}>No available technicians</td></tr>
          ) : filteredTechs.map((t) => (
            <tr key={t.ID}>
              <td className={styles.td}>
                <button style={{ background: "none", border: "none", color: "var(--color-primary)", cursor: "pointer", textDecoration: "underline", padding: 0 }} onClick={() => setEditTech({ ...t })}>{t.ID}</button>
              </td>
              <td className={styles.td}>{t.Firstname}</td>
              <td className={styles.td}>{t.Lastname}</td>
              <td className={styles.td}>{t.Email}</td>
              <td className={styles.td}>{t.Phone}</td>
              <td className={styles.td}>{t.Phone2}</td>
              <td className={styles.td}>{t.ExtraInfo}</td>
              <td className={styles.td}>
                <button className={styles.secondaryBtn} style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }} disabled={saving} onClick={() => handleAssign(t)}>
                  Assign ↓
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* ── Assigned Technicians ──────────────────────── */}
      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>Assigned technicians for location</h3>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: "var(--space-2)" }}>
        The technician at the top has the highest priority. Use the arrows to reorder. Click the ID to edit.
      </p>

      <div className={styles.tableScroll}>
      <table className={styles.table}>
        <thead>
          <tr>
            {techColumns.map((c) => <th key={c.key} className={styles.th}>{c.label}</th>)}
            <th className={styles.th}>Priority</th>
            <th className={styles.th}></th>
          </tr>
        </thead>
        <tbody>
          {assignedTechs.length === 0 ? (
            <tr><td className={styles.td} colSpan={techColumns.length + 2} style={{ textAlign: "center" }}>No technicians assigned</td></tr>
          ) : assignedTechs.map((t, idx) => (
            <tr key={t.ID}>
              <td className={styles.td}>
                <button style={{ background: "none", border: "none", color: "var(--color-primary)", cursor: "pointer", textDecoration: "underline", padding: 0 }} onClick={() => setEditTech({ ...t })}>{t.ID}</button>
              </td>
              <td className={styles.td}>{t.Firstname}</td>
              <td className={styles.td}>{t.Lastname}</td>
              <td className={styles.td}>{t.Email}</td>
              <td className={styles.td}>{t.Phone}</td>
              <td className={styles.td}>{t.Phone2}</td>
              <td className={styles.td}>{t.ExtraInfo}</td>
              <td className={styles.td}>
                <button className={styles.secondaryBtn} style={{ padding: "2px 6px", fontSize: "var(--text-xs)" }} disabled={saving || idx === 0} onClick={() => handleMovePrio(idx, -1)}>↑</button>
                {" "}
                <button className={styles.secondaryBtn} style={{ padding: "2px 6px", fontSize: "var(--text-xs)" }} disabled={saving || idx === assignedTechs.length - 1} onClick={() => handleMovePrio(idx, 1)}>↓</button>
              </td>
              <td className={styles.td}>
                <button className={styles.secondaryBtn} style={{ padding: "2px 8px", fontSize: "var(--text-xs)" }} disabled={saving} onClick={() => handleUnassign(t)}>
                  Remove ↑
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* ── Create New Technician ─────────────────────── */}
      <h3 className={styles.sectionTitle} style={{ marginTop: "var(--space-4)" }}>New Technician</h3>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", marginBottom: "var(--space-2)" }}>
        Create a new technician.
      </p>

      <div className={styles.fieldRow}>
        <label>Firstname</label>
        <input className={styles.fieldInput} value={newTech.Firstname} onChange={(e) => setNewTech({ ...newTech, Firstname: e.target.value })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Lastname</label>
        <input className={styles.fieldInput} value={newTech.Lastname} onChange={(e) => setNewTech({ ...newTech, Lastname: e.target.value })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Email</label>
        <input className={styles.fieldInput} value={newTech.Email} onChange={(e) => setNewTech({ ...newTech, Email: e.target.value })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Phone #1</label>
        <input className={styles.fieldInput} value={newTech.Phone} onChange={(e) => setNewTech({ ...newTech, Phone: e.target.value })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Phone #2</label>
        <input className={styles.fieldInput} value={newTech.Phone2} onChange={(e) => setNewTech({ ...newTech, Phone2: e.target.value })} />
      </div>
      <div className={styles.fieldRow}>
        <label>Extra Info</label>
        <input className={styles.fieldInput} value={newTech.ExtraInfo} onChange={(e) => setNewTech({ ...newTech, ExtraInfo: e.target.value })} />
      </div>
      <div style={{ marginTop: "var(--space-3)" }}>
        <button className={styles.primaryBtn} disabled={saving} onClick={handleCreateTech}>
          {saving ? "Creating…" : "Create"}
        </button>
      </div>

      {/* ── Edit Technician Modal ─────────────────────── */}
      {editTech && (
        <div className={styles.modalOverlay} onClick={() => setEditTech(null)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <h3 className={styles.sectionTitle}>Edit Technician (ID: {editTech.ID})</h3>
            <div className={styles.fieldRow}><label>Firstname</label><input className={styles.fieldInput} value={editTech.Firstname} onChange={(e) => setEditTech({ ...editTech, Firstname: e.target.value })} /></div>
            <div className={styles.fieldRow}><label>Lastname</label><input className={styles.fieldInput} value={editTech.Lastname} onChange={(e) => setEditTech({ ...editTech, Lastname: e.target.value })} /></div>
            <div className={styles.fieldRow}><label>Email</label><input className={styles.fieldInput} value={editTech.Email} onChange={(e) => setEditTech({ ...editTech, Email: e.target.value })} /></div>
            <div className={styles.fieldRow}><label>Phone</label><input className={styles.fieldInput} value={editTech.Phone} onChange={(e) => setEditTech({ ...editTech, Phone: e.target.value })} /></div>
            <div className={styles.fieldRow}><label>Phone #2</label><input className={styles.fieldInput} value={editTech.Phone2} onChange={(e) => setEditTech({ ...editTech, Phone2: e.target.value })} /></div>
            <div className={styles.fieldRow}><label>Extra Info</label><input className={styles.fieldInput} value={editTech.ExtraInfo} onChange={(e) => setEditTech({ ...editTech, ExtraInfo: e.target.value })} /></div>
            <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-3)" }}>
              <button className={styles.primaryBtn} disabled={saving} onClick={handleSaveEdit}>Save</button>
              <button className={styles.secondaryBtn} onClick={() => setEditTech(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
